import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import {
  getChemelexUiBannerDismissed,
  hasChemelexUiFeature
} from 'src/module/authentication/chemelexUi.selector';
import { updateUserPreferences } from 'src/module/user/preferences';
import { getUpdateUserLoading } from 'src/module/user/selector';
import useSwitchChemelexUi from 'src/component/ds/useSwitchChemelexUi';
import DsRoot from 'src/component/ds/DsRoot';
import { Alert, AlertAction, AlertDescription, AlertTitle } from 'src/component/ds/ui/alert';
import { Button } from 'src/component/ds/ui/button';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';

export default function ChemelexUiBanner ({ isV2 }: { isV2: boolean }) {
  const dispatch = useDispatch();
  const hasFeature = useSelector(hasChemelexUiFeature);
  const isDismissed = useSelector(getChemelexUiBannerDismissed);
  const isUpdatingUser = useSelector(getUpdateUserLoading);
  const switchChemelexUi = useSwitchChemelexUi();
  const { t } = useTranslation();

  const [pendingSave, setPendingSave] = useState<'switch' | 'dismiss' | null>(null);
  useEffect(() => {
    if (!isUpdatingUser) {
      setPendingSave(null);
    }
  }, [isUpdatingUser]);
  const isSaving = pendingSave !== null && isUpdatingUser;
  const isSwitching = isSaving && pendingSave === 'switch';

  if (!hasFeature || isDismissed) {
    return null;
  }

  let title = 'chemelexUiBanner.onClassic';
  let description = 'chemelexUiBanner.onClassicDescription';
  let action = 'chemelexUiBanner.switchToUpdated';
  let pendingAction = 'chemelexUiBanner.switchingToUpdated';
  if (isV2) {
    title = 'chemelexUiBanner.onUpdated';
    description = 'chemelexUiBanner.onUpdatedDescription';
    action = 'chemelexUiBanner.switchToClassic';
    pendingAction = 'chemelexUiBanner.switchingToClassic';
  }

  const handleSwitch = () => {
    setPendingSave('switch');
    switchChemelexUi(!isV2);
  };
  const handleDismiss = () => {
    setPendingSave('dismiss');
    dispatch(updateUserPreferences({ chemelexUiBannerDismissed: true }));
  };

  return (
    <DsRoot>
      {/* A persistent notice, not a live alert, so it isn't announced on every page. */}
      <Alert
        role='region'
        aria-label={t('chemelexUiBanner.label')}
        className={
          'ds-rounded-none ds-border-0 ds-bg-tab-active ds-text-primary-foreground ' +
          'has-[>svg]:ds-grid-cols-[auto_1fr_auto] has-[[data-slot=alert-action]]:ds-pr-4'
        }
      >
        <InfoOutlined aria-hidden='true' />
        <AlertTitle className='ds-font-semibold'>{t(title)}</AlertTitle>
        <AlertDescription className='ds-text-primary-foreground'>{t(description)}</AlertDescription>
        <AlertAction className='ds-static ds-col-start-3 ds-row-span-2 ds-row-start-1 ds-flex ds-items-center ds-gap-2 ds-self-center'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleSwitch}
            disabled={isSaving}
            className={cn(
              'ds-border-primary-foreground ds-bg-transparent ds-text-primary-foreground hover:ds-bg-primary-hover',
              isSwitching && 'disabled:ds-opacity-100'
            )}
          >
            {t(isSwitching ? pendingAction : action)}
          </Button>
          <Button
            variant='ghost'
            size='icon-sm'
            onClick={handleDismiss}
            disabled={isSaving}
            aria-label={t('chemelexUiBanner.dismiss')}
            className='ds-text-xl ds-text-primary-foreground hover:ds-bg-primary-hover'
          >
            <CloseIcon fontSize='inherit' />
          </Button>
        </AlertAction>
      </Alert>
    </DsRoot>
  );
}
